import { h } from "@stencil/core";
import { isBreakpointEqualOrSmaller, createUuid, getCurrentBreakpoint } from "../../utils";
export class DcUiHeader {
    constructor() {
        this.lastScrollY = 0;
        this.headerHeight = 0;
        this.metaNavElements = [];
        this.originalPositions = new Map();
        this.insideTrigger = false;
        this.currentBreakpoint = getCurrentBreakpoint();
        this.handleResize = () => {
            const newBreakpoint = getCurrentBreakpoint();
            if (this.currentBreakpoint !== newBreakpoint) {
                // Disconnect the observer after first mutation
                this.metaNavObserver.disconnect();
                this.currentBreakpoint = newBreakpoint;
                this.updateLanguageButtonPosition();
            }
        };
        this.updateLanguageButtonPosition = () => {
            if (!this.metaNavSlot || !this.mainNavLanguageSlot)
                return;
            // Handle language button
            const languageButton = this.metaNavElements.find((el) => el.hasAttribute('language'));
            if (languageButton) {
                const targetParent = isBreakpointEqualOrSmaller('xl')
                    ? this.mainNavLanguageSlot
                    : this.metaNavSlot;
                const position = this.originalPositions.get(languageButton.id);
                this.moveElement(languageButton, targetParent, position !== null && position !== void 0 ? position : null, isBreakpointEqualOrSmaller('xl'));
            }
            // Handle other meta-nav elements
            const otherMetaNavElements = this.metaNavElements.filter((el) => !el.hasAttribute('language'));
            const isMdOrSmaller = isBreakpointEqualOrSmaller('md');
            otherMetaNavElements.forEach((element) => {
                const targetParent = isMdOrSmaller ? this.mainNavLanguageSlot : this.metaNavSlot;
                const position = this.originalPositions.get(element.id);
                this.moveElement(element, targetParent, position !== null && position !== void 0 ? position : null, isMdOrSmaller);
            });
            this.languageButtonMoved.emit();
        };
        this.handleScroll = () => {
            var _a, _b, _c;
            const currentScrollY = window.scrollY;
            if (currentScrollY > this.headerHeight) {
                if (currentScrollY > this.lastScrollY) {
                    // Scrolling down - hide header
                    (_a = this.headerRef) === null || _a === void 0 ? void 0 : _a.classList.add('header--hidden');
                    this.dcHeaderHidden.emit();
                }
                else {
                    // Scrolling up - show header
                    (_b = this.headerRef) === null || _b === void 0 ? void 0 : _b.classList.remove('header--hidden');
                    this.dcHeaderShown.emit();
                }
            }
            else {
                // At the top - ensure header is visible
                (_c = this.headerRef) === null || _c === void 0 ? void 0 : _c.classList.remove('header--hidden');
                this.dcHeaderShown.emit();
            }
            this.lastScrollY = currentScrollY;
        };
        this.handleBeforeUnload = () => {
            if (this.insideTrigger) {
                this.insideTrigger = false;
                return;
            }
            // because the trigger is not inside the component, we can remove the session keys beginning with 'nav-path-'
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith('nav-path-')) {
                    sessionStorage.removeItem(key);
                }
            });
        };
    }
    async manageSession(sessionKey) {
        // delete all the other session keys but the one we want to keep
        Object.keys(sessionStorage).forEach((key) => {
            if (key !== sessionKey) {
                sessionStorage.removeItem(key);
            }
        });
        this.insideTrigger = true;
    }
    componentDidLoad() {
        var _a, _b;
        this.headerRef = (_a = this.el.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.header-wrapper');
        this.headerHeight = ((_b = this.headerRef) === null || _b === void 0 ? void 0 : _b.offsetHeight) || 0;
        // Get references to elements
        this.metaNavSlot = this.el.querySelector('dc-ui-header-base');
        this.mainNavLanguageSlot = this.el.querySelector('dc-ui-header-main-nav');
        this.mainNavElement = this.el.querySelector('dc-ui-header-main-nav');
        // Add event listeners
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);
        // Store all meta-nav elements and their positions with unique IDs
        this.storeMetaNavElements();
        // Setup observer for meta nav changes
        this.setupMetaNavObserver();
        // Initial position check
        this.updateLanguageButtonPosition();
        // Add beforeunload listener
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }
    setupMetaNavObserver() {
        if (this.metaNavSlot) {
            this.metaNavObserver = new MutationObserver((mutations) => {
                const hasChildListChanges = mutations.some((mutation) => mutation.type === 'childList' &&
                    (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0));
                if (hasChildListChanges) {
                    // Store elements and update positions
                    this.storeMetaNavElements();
                    this.updateLanguageButtonPosition();
                }
            });
            this.metaNavObserver.observe(this.metaNavSlot, {
                childList: true,
            });
        }
    }
    storeMetaNavElements() {
        if (this.metaNavSlot) {
            this.metaNavElements = Array.from(this.el.querySelectorAll('dc-ui-meta-nav-button, dc-ui-meta-nav-link'));
            const currentParent = this.metaNavElements[0].parentElement;
            const children = Array.from(currentParent.children);
            this.metaNavElements.forEach((el) => {
                // Use existing ID or create a unique one
                const id = el.id || `meta-nav-${createUuid()}`;
                if (!el.id)
                    el.id = id;
                this.originalPositions.set(id, children.indexOf(el));
            });
        }
    }
    componentDidRender() {
        if (this.mainNavElement) {
            this.mainNavElement.checkForOverflow();
        }
    }
    disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        // Disconnect the observer
        if (this.metaNavObserver) {
            this.metaNavObserver.disconnect();
        }
    }
    moveElement(element, targetParent, referencePosition, useSmallLayout) {
        const currentParent = element.parentElement;
        if (currentParent === targetParent)
            return;
        currentParent === null || currentParent === void 0 ? void 0 : currentParent.removeChild(element);
        if (targetParent === this.mainNavLanguageSlot) {
            element.setAttribute('data-in-main-nav', '');
        }
        else {
            element.removeAttribute('data-in-main-nav');
        }
        if (!useSmallLayout && targetParent.children.length > 0 && referencePosition !== null) {
            const referenceNode = targetParent.children[referencePosition] || null;
            targetParent.insertBefore(element, referenceNode);
        }
        else {
            targetParent.appendChild(element);
        }
    }
    render() {
        const hasBase = this.el.querySelector('[slot="base"]');
        const hasMainNav = this.el.querySelector('[slot="main-nav"]');
        const base = hasBase ? (h("div", { class: "header-base-wrapper" }, h("slot", { name: "base" }))) : null;
        const mainNav = hasMainNav ? (h("div", { class: "header-main-nav-wrapper" }, h("slot", { name: "main-nav" }))) : null;
        return (h("header", { key: '47f4af65beb82c7b7e0cc4e6b423dbb9c27d934c', class: "header-wrapper" }, base, mainNav, h("slot", { key: '5043029a522bcce81c45968e11bf6821e59803c5', name: "popover" })));
    }
    static get is() { return "dc-ui-header"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["dc-ui-header.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dc-ui-header.css"]
        };
    }
    static get events() {
        return [{
                "method": "dcHeaderHidden",
                "name": "dcHeaderHidden",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "dcHeaderShown",
                "name": "dcHeaderShown",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "languageButtonMoved",
                "name": "languageButtonMoved",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "manageSession": {
                "complexType": {
                    "signature": "(sessionKey: string) => Promise<void>",
                    "parameters": [{
                            "name": "sessionKey",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
}
//# sourceMappingURL=dc-ui-header.js.map
